import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { api } from '../../services/api';

export default function ResetPasswordPage(): React.ReactElement {
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [apiError, setApiError] = useState('');
  const [success, setSuccess] = useState(false);

  const schema = z
    .object({
      password: z.string().min(8, tCommon('validation.minLength', { min: 8 })),
      confirm_password: z.string().min(1, tCommon('validation.required')),
    })
    .refine((data) => data.password === data.confirm_password, {
      message: tCommon('validation.passwordMatch'),
      path: ['confirm_password'],
    });

  type ResetForm = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetForm>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: ResetForm): Promise<void> => {
    setApiError('');
    try {
      await api.post('/v1/auth/reset-password', {
        token,
        new_password: data.password,
      });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch {
      setApiError(t('errors.tokenExpired'));
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-start justify-center pt-20 px-4">
        <div className="alert alert-error max-w-md w-full">
          <span>{t('errors.tokenInvalid')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-start justify-center pt-20 px-4">
      <div className="card bg-base-100 shadow-xl w-full max-w-md">
        <div className="card-body">
          <h2 className="card-title text-3xl font-bold justify-center mb-4">
            {t('resetPassword.title')}
          </h2>

          {success && (
            <div className="alert alert-success mb-4">
              <span>{t('resetPassword.success')}</span>
            </div>
          )}

          {apiError && (
            <div className="alert alert-error mb-4">
              <span>{apiError}</span>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="form-control w-full mb-3">
              <label className="label">
                <span className="label-text">{t('resetPassword.password')}</span>
              </label>
              <input
                type="password"
                className={`input input-bordered w-full ${errors.password ? 'input-error' : ''}`}
                {...register('password')}
              />
              {errors.password && (
                <label className="label">
                  <span className="label-text-alt text-error">{errors.password.message}</span>
                </label>
              )}
            </div>

            <div className="form-control w-full mb-3">
              <label className="label">
                <span className="label-text">{t('resetPassword.confirmPassword')}</span>
              </label>
              <input
                type="password"
                className={`input input-bordered w-full ${errors.confirm_password ? 'input-error' : ''}`}
                {...register('confirm_password')}
              />
              {errors.confirm_password && (
                <label className="label">
                  <span className="label-text-alt text-error">{errors.confirm_password.message}</span>
                </label>
              )}
            </div>

            <button
              type="submit"
              className="btn btn-primary w-full mt-2 mb-2"
              disabled={isSubmitting || success}
            >
              {t('resetPassword.submit')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
